import { ArrowSquareOutIcon } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { Card } from "@volca/ui/components/ui/card";
import { INSPECTION_CAMERA } from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import type { ApPurchaseDocument } from "@/accounts-payable/types";
import { useApInvoiceDocument } from "@/accounts-payable/use-invoice-document";
import { documentLabel } from "@/accounts-payable/utils";
import { DocumentFrame } from "./document-frame";

/** The document preview for any purchase document's review page. */
export const DocumentReviewPreview = ({
  document: purchaseDocument,
}: {
  document: ApPurchaseDocument;
}) => {
  const kind = purchaseDocument.document_type ?? "invoice";
  const label = documentLabel(purchaseDocument);
  const {
    objectUrl: documentUrl,
    isLoading: documentLoading,
    isError: documentError,
  } = useApInvoiceDocument(purchaseDocument.file_url);

  return (
    <Stack
      align="start"
      gap={2}
      className="w-full self-start lg:sticky lg:top-4"
    >
      <Card className="flex min-h-[60cqh] w-full flex-col gap-0 overflow-hidden py-0 sm:min-h-[78cqh]">
        {/* Toolbar */}
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap={2}
          className="w-full flex-none border-b bg-surface-raised px-4 py-2.5"
        >
          <Stack direction="row" align="center" gap={2} className="min-w-0">
            <FileTextIcon className="h-4 w-4 flex-none text-subtle" />
            <span
              className="truncate text-sm font-semibold"
              title={
                purchaseDocument.attachment?.file_name ??
                `${purchaseDocument.document_number ?? kind}.pdf`
              }
            >
              {purchaseDocument.attachment?.file_name ??
                `${purchaseDocument.document_number ?? kind}.pdf`}
            </span>
          </Stack>
          {documentUrl && (
            <a
              href={documentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-none items-center gap-1 text-sm font-semibold text-link hover:underline"
            >
              Open
              <ArrowSquareOutIcon className="h-3.5 w-3.5" />
            </a>
          )}
        </Stack>

        {/* Body */}
        {purchaseDocument?.file_url ? (
          documentError ? (
            <Stack
              align="center"
              justify="center"
              gap={1}
              className="h-[40cqh] w-full bg-neutral/40 text-center"
            >
              <FileTextIcon className="h-8 w-8 text-subtle" />
              <span className="text-subtle">
                Couldn’t load the {kind} document.
              </span>
            </Stack>
          ) : !documentUrl || documentLoading ? (
            // The size the document will take, so nothing moves when it lands.
            <Skeleton className="h-[60cqh] w-full rounded-none sm:h-[78cqh]" />
          ) : purchaseDocument.file_content_type?.startsWith("image/") ? (
            <Stack
              align="start"
              gap={0}
              className="max-h-[78cqh] w-full overflow-auto bg-neutral/40 p-4"
            >
              <img
                src={documentUrl}
                alt={label}
                className="mx-auto w-full max-w-[820px] rounded-md shadow-raised"
              />
            </Stack>
          ) : (
            // Keyed by the document, so the next one starts hidden again.
            <DocumentFrame key={documentUrl} src={documentUrl} title={label} />
          )
        ) : (
          <MachineEmptyState
            art={INSPECTION_CAMERA}
            title="No document attached"
            description={`You can still review this ${kind} using the details on this page.`}
            footnote="This preview shows the original file when one is available."
          />
        )}
      </Card>
    </Stack>
  );
};
